'use client';
import dynamic from 'next/dynamic';
import { FC, useEffect, useMemo } from 'react';
import LocaleFirstBootstrap from '@/components/common/locale-first-bootstrap/locale-first-bootstrap';
import { THEME_NAMES, type ThemeName } from '@/hero';
import AppProvider, { AppProviderProps } from '@/providers/app-provider/app-provider';
import DeploymentVersionReconciler from '@/providers/deployment-version-reconciler';
import RootProvider from '@/providers/root-provider';

const SelectDefaultCompanyModal = dynamic(
  () => import('@/providers/select-default-company-modal/select-default-company-modal'),
  { ssr: false },
);

const CompanyChangeModalProvider = dynamic(
  () => import('@/components/common/company-change-modal-provider/company-change-modal-provider'),
  { ssr: false },
);

const CrossUpsellDialog = dynamic(
  () => import('@/components/common/cross-upsell-dialog/cross-upsell-dialog'),
  { ssr: false },
);

const OrderReviewPrompt = dynamic(
  () => import('@/components/common/order-review-prompt/order-review-prompt'),
  { ssr: false },
);

type Props = Partial<AppProviderProps>;

const Page: FC<Props> = ({ children, context, t }) => {
  const coreConfigFromContext =
    context && typeof context === 'object' && 'coreConfig' in context
      ? (context.coreConfig as
          | {
              theme?: string;
              defaultLanguageCode?: string;
              supportedLanguages?: Array<{ code: string }>;
            }
          | undefined)
      : undefined;
  const themeFromConfig = coreConfigFromContext?.theme;
  const activeTheme: ThemeName = THEME_NAMES.includes(themeFromConfig as ThemeName)
    ? (themeFromConfig as ThemeName)
    : 'bndk';
  const defaultLocale = coreConfigFromContext?.defaultLanguageCode;

  // Keyed by the joined codes so the context value stays referentially stable across renders.
  const supportedLanguagesKey = (coreConfigFromContext?.supportedLanguages ?? [])
    .map(language => language.code)
    .join(',');
  const supportedLocales = useMemo(
    () => supportedLanguagesKey.split(',').filter(Boolean),
    [supportedLanguagesKey],
  );

  useEffect(() => {
    document.body.classList.remove(...THEME_NAMES);
    document.body.classList.add(activeTheme);
  }, [activeTheme]);

  return (
    <RootProvider defaultLocale={defaultLocale} supportedLocales={supportedLocales}>
      <AppProvider t={t} context={context}>
        <LocaleFirstBootstrap />
        <DeploymentVersionReconciler />
        <div className={activeTheme}>
          {children}
          <SelectDefaultCompanyModal />
          <CompanyChangeModalProvider />
          <CrossUpsellDialog />
          <OrderReviewPrompt />
        </div>
      </AppProvider>
    </RootProvider>
  );
};

export default Page;
